import React, { useState, useEffect } from 'react';
import './DrugSelector.css';

// API configuration - Railway uses full URL, Codespace uses relative URLs with proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface ApprovedDrug {
  id: number;
  category: string;
  agent_name: string;
  species: string;
  dose: string;
  route: string;
  frequency: string;
  is_controlled_substance: boolean;
}

interface DrugEntry {
  id: string;
  drug: string;
  dose: string;
  frequency: string;
  route: string;
  otherRoute: string;
  isApproved?: boolean;
  controlledSubstance?: boolean;
}

interface DrugSelectorProps {
  category: 'anesthetics' | 'analgesics';
  species: 'M' | 'R';
  entries: DrugEntry[];
  onEntriesChange: (entries: DrugEntry[]) => void;
}

const DrugSelector: React.FC<DrugSelectorProps> = ({
  category,
  species,
  entries,
  onEntriesChange
}) => {
  const [approvedDrugs, setApprovedDrugs] = useState<ApprovedDrug[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    fetchApprovedDrugs();
  }, [category, species]);

  const fetchApprovedDrugs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/approved-drugs?category=${category}&species=${species}`);
      const data = await response.json();
      setApprovedDrugs(data.drugs || []);
    } catch (error) {
      console.error('Failed to fetch approved drugs:', error);
    } finally {
      setLoading(false);
    }
  };

  const addDrugFromApproved = (drug: ApprovedDrug) => {
    const newEntry: DrugEntry = {
      id: Date.now().toString(),
      drug: drug.agent_name,
      dose: drug.dose,
      frequency: drug.frequency,
      route: drug.route === 'Other' ? '' : drug.route,
      otherRoute: drug.route === 'Other' ? drug.route : '',
      isApproved: true,
      controlledSubstance: drug.is_controlled_substance
    };
    
    onEntriesChange([...entries, newEntry]);
    setShowSelector(false);
  };

  const addCustomDrug = () => {
    const newEntry: DrugEntry = {
      id: Date.now().toString(),
      drug: '',
      dose: '',
      frequency: '',
      route: '',
      otherRoute: '',
      isApproved: false,
      controlledSubstance: false
    };
    
    onEntriesChange([...entries, newEntry]);
  };

  const updateEntry = (id: string, field: keyof DrugEntry, value: string) => {
    const updatedEntries = entries.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    );
    onEntriesChange(updatedEntries);
  };

  const removeEntry = (id: string) => {
    onEntriesChange(entries.filter(entry => entry.id !== id));
  };

  const categoryTitle = category === 'anesthetics' ? 'Anesthetics' : 'Analgesics';

  return (
    <div className="drug-selector">
      <div className="drug-selector-header">
        <h4>{categoryTitle}</h4>
        <div className="drug-selector-buttons">
          <button
            type="button"
            onClick={() => setShowSelector(!showSelector)}
            className="btn btn-pennington-primary btn-sm me-2"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Quick Select'}
          </button>
          <button
            type="button"
            onClick={addCustomDrug}
            className="btn btn-pennington-secondary btn-sm"
          >
            Add Custom
          </button>
        </div>
      </div>

      {showSelector && (
        <div className="approved-drugs-list mt-3 p-3 bg-light rounded">
          <h6>Approved {categoryTitle} for {species === 'M' ? 'Mice' : 'Rats'}</h6>
          {approvedDrugs.length === 0 ? (
            <p className="text-muted">No approved drugs found for this category and species.</p>
          ) : (
            <div className="row">
              {approvedDrugs.map(drug => (
                <div key={drug.id} className="col-md-6 mb-2">
                  <div className="card">
                    <div className="card-body p-2">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong>{drug.agent_name}</strong>
                          {drug.is_controlled_substance && (
                            <span className="badge bg-warning text-dark ms-1">Controlled</span>
                          )}
                          <br />
                          <small className="text-muted">
                            {drug.dose} • {drug.route} • {drug.frequency}
                          </small>
                        </div>
                        <button
                          type="button"
                          onClick={() => addDrugFromApproved(drug)}
                          className="btn btn-primary btn-sm"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="drug-entries">
        {entries.length === 0 ? (
          <div className="text-muted text-center p-4" style={{
            background: 'var(--pennington-light-gray)',
            borderRadius: '12px',
            border: '2px dashed #dee2e6',
            fontStyle: 'italic'
          }}>
            No {category} added yet. Use Quick Select or Add Custom to get started.
          </div>
        ) : (
          <div className="drug-entries-container">
            {entries.map((entry, index) => (
              <div key={entry.id} className={`drug-entry ${entry.isApproved ? 'approved-drug-entry' : 'custom-drug-entry'}`}>
                <div className="drug-entry-header">
                  <div className="drug-entry-title">
                    <h5>{categoryTitle.slice(0, -1)} {index + 1}</h5>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEntry(entry.id)}
                    className="btn btn-outline-danger btn-sm"
                    style={{ flexShrink: 0 }}
                  >
                    Remove
                  </button>
                </div>
                
                {entry.isApproved ? (
                  // Approved Drug - Read-only display
                  <div className="approved-drug-display">
                    <div className="drug-field">
                      <label className="form-label">Drug/Agent</label>
                      <div className="form-control-plaintext">{entry.drug}</div>
                    </div>
                    <div className="drug-field">
                      <label className="form-label">Dose</label>
                      <div className="form-control-plaintext">{entry.dose}</div>
                    </div>
                    <div className="drug-field">
                      <label className="form-label">Frequency</label>
                      <div className="form-control-plaintext">{entry.frequency}</div>
                    </div>
                    <div className="drug-field">
                      <label className="form-label">Route</label>
                      <div className="form-control-plaintext">{entry.route}</div>
                    </div>
                  </div>
                ) : (
                  // Custom Drug - Editable form
                  <div className="custom-drug-form">
                    <div>
                      <label className="form-label">Drug/Agent</label>
                      <input
                        type="text"
                        className="form-control"
                        value={entry.drug}
                        onChange={(e) => updateEntry(entry.id, 'drug', e.target.value)}
                        placeholder="Enter drug name"
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Dose</label>
                      <input
                        type="text"
                        className="form-control"
                        value={entry.dose}
                        onChange={(e) => updateEntry(entry.id, 'dose', e.target.value)}
                        placeholder="Enter dose"
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Frequency</label>
                      <input
                        type="text"
                        className="form-control"
                        value={entry.frequency}
                        onChange={(e) => updateEntry(entry.id, 'frequency', e.target.value)}
                        placeholder="Enter frequency"
                      />
                    </div>
                    
                    <div>
                      <label className="form-label">Route</label>
                      <select
                        className="form-select"
                        value={entry.route}
                        onChange={(e) => updateEntry(entry.id, 'route', e.target.value)}
                      >
                        <option value="">Select route</option>
                        <option value="SC">SC (Subcutaneous)</option>
                        <option value="IP">IP (Intraperitoneal)</option>
                        <option value="PO">PO (Oral)</option>
                        <option value="Inhalation">Inhalation</option>
                        <option value="Incisional">Incisional</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    
                    {entry.route === 'Other' && (
                      <div>
                        <label className="form-label">Specify other route</label>
                        <input
                          type="text"
                          className="form-control"
                          value={entry.otherRoute}
                          onChange={(e) => updateEntry(entry.id, 'otherRoute', e.target.value)}
                          placeholder="Specify route"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DrugSelector;